import React from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import MediaThumb from './MediaThumb';
import { formatEpoch, isVideoSubType } from '../config';

const Field = ({ label, value }) =>
  value ? (
    <div className="text-xs">
      <span className="font-semibold text-slate-500">{label}: </span>
      <span className="text-slate-700">{value}</span>
    </div>
  ) : null;

/** Newest-first version history, straight from `/versions`. */
const VersionsList = ({ versions, loading, error, onRetry, onZoom }) => {
  if (loading) {
    return (
      <div className="space-y-3 p-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex gap-3">
            <div className="h-14 w-14 shrink-0 animate-pulse rounded-lg bg-slate-200" />
            <div className="flex-1 space-y-2 py-1">
              <div className="h-3 w-1/3 animate-pulse rounded bg-slate-200" />
              <div className="h-2.5 w-1/2 animate-pulse rounded bg-slate-200" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-4 rounded-lg border border-rose-200 bg-rose-50 p-4 text-center">
        <p className="text-sm text-rose-800">{error}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-rose-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!versions.length) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-sm font-medium text-slate-700">No versions yet</p>
        <p className="mt-1 text-xs text-slate-500">
          Every design you upload is kept here so you can compare it with earlier ones.
        </p>
      </div>
    );
  }

  return (
    <ol className="space-y-3 p-4">
      {versions.map((v, i) => {
        const f = v?.fields || {};
        const video = isVideoSubType(v?.subType);
        return (
          <li
            key={`${v?.version ?? i}-${v?.createdAt ?? i}`}
            className={`rounded-xl border p-3 ${
              v?.isCurrent ? 'border-sky-200 bg-sky-50/50' : 'border-slate-200 bg-white'
            }`}
          >
            <div className="flex items-start gap-3">
              <MediaThumb
                src={f.imgUrls}
                isVideo={video && !!f.videoUrls}
                alt={`Version ${v?.version}`}
                className="h-14 w-14"
                onClick={f.imgUrls ? () => onZoom?.(f.imgUrls, false) : undefined}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-slate-900">
                    Version {v?.version ?? i + 1}
                  </span>
                  {v?.isCurrent && (
                    <span className="rounded-full bg-sky-600 px-2 py-[2px] text-[10px] font-semibold uppercase tracking-wide text-white">
                      Current
                    </span>
                  )}
                </div>
                <p className="mt-0.5 break-words text-xs text-slate-600">
                  {v?.summary || 'Created'}
                </p>
                <p className="mt-1 text-[11px] text-slate-400">
                  {v?.userName ? `${v.userName}` : 'System'}
                  {v?.userType ? ` · ${v.userType}` : ''} · {formatEpoch(v?.createdAt)}
                </p>

                <div className="mt-2 space-y-1">
                  <Field label="Name" value={f.creativeName} />
                  <Field label="Headline" value={f.headLine} />
                  <Field label="Description" value={f.description} />
                  <Field label="Link text" value={f.textInLink} />
                </div>

                {f.videoUrls ? (
                  <button
                    type="button"
                    onClick={() => onZoom?.(f.videoUrls, true)}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:underline"
                  >
                    <SlIcon library="lucide" name="play-circle" style={{ fontSize: '13px' }} />
                    Play video
                  </button>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default VersionsList;
